import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  DeepResearchToolType,
  ReadBenchlingDocumentToolCall,
  ProgressToolCall,
  TOOL_DESCRIPTIONS,
  TOOL_ICONS,
} from 'llm/deep-research/tool-details/types';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';

export interface CitationSourceChipProps {
  citationId: string;
  citationNumber: number;
  onCitationClick: (toolId: string, isSourceCitation?: boolean) => void;
  toolCall: ProgressToolCall | undefined;
}

export default function CitationSourceChip({
  citationId,
  citationNumber,
  onCitationClick,
  toolCall,
}: CitationSourceChipProps): JSX.Element {
  const handleClick = React.useCallback((): void => {
    onCitationClick(citationId, true);
  }, [onCitationClick, citationId]);

  const toolDescription = toolCall ? TOOL_DESCRIPTIONS[toolCall.tool] : 'View citation details';

  const tooltipContent = (
    <div>
      <div>Activity: {toolDescription}</div>
      <div>Citation #{citationNumber}</div>
    </div>
  );

  const notebookId =
    toolCall?.tool && toolCall.tool === DeepResearchToolType.ReadBenchlingDocumentTool
      ? (toolCall as ReadBenchlingDocumentToolCall).notebookId
      : undefined;

  return (
    <li className={css(styles.container)} value={citationNumber}>
      <Tooltip text={tooltipContent} placement={FloatingPlacement.TOP}>
        {notebookId ? (
          <EntryChip id={notebookId} size={ChipSize.SMALL} onClick={handleClick} />
        ) : (
          <Chip
            size={ChipSize.SMALL}
            text={toolCall ? toolCall.chipName : ''}
            onClick={handleClick}
            icon={toolCall ? TOOL_ICONS[toolCall.tool] : undefined}
          />
        )}
      </Tooltip>
    </li>
  );
}

export interface WebCitationSourceProps {
  url: string;
}

export function WebCitationSource({url}: WebCitationSourceProps): JSX.Element | null {
  let cleanUrl = '';
  let domain = '';
  let isValidUrl = true;

  try {
    const urlObj = new URL(url);
    cleanUrl = `${urlObj.origin}${urlObj.pathname}`;
    domain = urlObj.hostname;
  } catch (error) {
    if (error instanceof TypeError) {
      isValidUrl = false;
      cleanUrl = url;
      domain = 'Invalid URL';
    } else {
      throw error;
    }
  }

  const handleClick = React.useCallback((): void => {
    window.open(cleanUrl, '_blank', 'noopener,noreferrer');
  }, [cleanUrl]);

  if (!isValidUrl) {
    return null;
  }

  return (
    <li className={css(styles.webCitationItem)}>
      <Tooltip text={cleanUrl} placement={FloatingPlacement.TOP}>
        <Chip size={ChipSize.SMALL} text={domain} onClick={handleClick} icon="globe" />
      </Tooltip>
    </li>
  );
}

const styles = StyleSheet.create({
  container: {
    listStylePosition: 'inside',
  },
  citationNumber: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  webCitationItem: {
    listStyle: 'none',
  },
});
